/**
 * Prime Service (MIRA 2.0)
 *
 * "Find PRIME-medlem" — camelCase-API (kontrakt §5). Mappere er near-identity
 * + null-sikring; eksporteret for unit-tests. Modulet nøgles på `handle`.
 */

import { apiGet, apiPost, apiDelete } from './api';
import { normalizePhone } from '../utils/formatters';
import type {
  PrimeFamilyMember, PrimeMember, PrimeSearchResult,
  PrimeStatus, PrimeMemberType, PrimePlan, ReactivateResult, SyncResult,
} from '../types/prime.types';

// ---- Raw shapes (camelCase som returneret af MIRA 2.0) ----

interface RawFamilyMember {
  id?: string | null;
  name?: string | null;
  phone: string;
  status: PrimeStatus;
  birthday?: string | null;
  invitedAt?: string | null;
  pending?: boolean;
}

interface RawMember {
  handle: string;
  name: string;
  status: PrimeStatus;
  type: PrimeMemberType;
  email?: string | null;
  phone: string;
  birthday?: string | null;
  memberSince?: string | null;
  plan?: PrimePlan;
  cancelledPending?: boolean;
  coverageEndsAt?: string | null;
  family?: RawFamilyMember[] | null;
  parentName?: string | null;
  parentPhone?: string | null;
}

interface RawSearchResponse {
  found: boolean;
  member?: RawMember | null;
}

// ---- Mappers (exported for tests) ----

export function mapFamilyMember(raw: RawFamilyMember): PrimeFamilyMember {
  return {
    id: raw.id ?? null,
    name: raw.name ?? null,
    phone: raw.phone,
    status: raw.status,
    birthday: raw.birthday ?? null,
    invitedAt: raw.invitedAt ?? null,
    pending: !!raw.pending,
  };
}

export function mapMember(raw: RawMember): PrimeMember {
  return {
    handle: raw.handle,
    name: raw.name,
    status: raw.status,
    type: raw.type,
    email: raw.email ?? null,
    phone: raw.phone,
    birthday: raw.birthday ?? null,
    memberSince: raw.memberSince ?? null,
    plan: raw.plan ?? null,
    cancelledPending: !!raw.cancelledPending,
    coverageEndsAt: raw.coverageEndsAt ?? null,
    family: (raw.family ?? []).map(mapFamilyMember),
    parentName: raw.parentName ?? null,
    parentPhone: raw.parentPhone ?? null,
  };
}

export function mapSearchResult(raw: RawSearchResponse): PrimeSearchResult {
  if (!raw.found || !raw.member) return { found: false, member: null };
  return { found: true, member: mapMember(raw.member) };
}

// ---- Endpoints ----

const enc = encodeURIComponent;

/** GET søg medlem på mobil. Svaret returnerer `handle` — gem det i state. */
export async function searchMember(
  phone: string, countryCode = 45
): Promise<PrimeSearchResult> {
  const data = await apiGet<RawSearchResponse>('/portal/prime/members', {
    query: { phone: normalizePhone(phone), country_code: countryCode },
  });
  return mapSearchResult(data);
}

/** POST inviter NYT hovedmedlem (SMS-link). Returnerer om SMS blev sendt. */
export async function inviteMember(
  phone: string, countryCode = 45
): Promise<boolean> {
  // country_code SKAL sendes som tekststreng i body (backend afviser tal med 422).
  const data = await apiPost<{ sent: boolean }>('/portal/prime/members/invite', {
    body: { phone: normalizePhone(phone), country_code: String(countryCode) },
  });
  return !!data.sent;
}

/** POST tilføj familiemedlem (max 5 håndhæves i UI). Returnerer ny familie-liste. */
export async function addFamilyMember(
  handle: string, phone: string, countryCode = 45
): Promise<PrimeFamilyMember[]> {
  const data = await apiPost<{ family: RawFamilyMember[] }>(
    `/portal/prime/members/${enc(handle)}/family`,
    // country_code SKAL sendes som tekststreng i body (backend afviser tal med 422).
    { body: { phone: normalizePhone(phone), country_code: String(countryCode) } }
  );
  return data.family.map(mapFamilyMember);
}

/** DELETE fjern familiemedlem. Returnerer ny familie-liste. */
export async function removeFamilyMember(
  handle: string, familyId: string
): Promise<PrimeFamilyMember[]> {
  const data = await apiDelete<{ family: RawFamilyMember[] }>(
    `/portal/prime/members/${enc(handle)}/family/${enc(familyId)}`
  );
  return data.family.map(mapFamilyMember);
}

/** POST annullér medlemskab. Default graceful; `immediate` udløber øjeblikkeligt. */
export async function cancelMember(
  handle: string, opts: { immediate?: boolean; reason?: string } = {}
): Promise<PrimeMember> {
  const body: Record<string, unknown> = {};
  if (opts.immediate) body.immediate = true;
  if (opts.reason) body.reason = opts.reason;
  const data = await apiPost<{ member: RawMember }>(
    `/portal/prime/members/${enc(handle)}/cancel`,
    { body }
  );
  return mapMember(data.member);
}

/** POST smart genaktivér (uncancel/resubscribe). `plan` kræves ved resubscribe. */
export async function reactivateMember(
  handle: string, plan?: 'monthly' | 'yearly'
): Promise<ReactivateResult> {
  const body: Record<string, unknown> = {};
  if (plan) body.plan = plan;
  const data = await apiPost<{ member: RawMember; mode: string }>(
    `/portal/prime/members/${enc(handle)}/reactivate`,
    { body }
  );
  return { member: mapMember(data.member), mode: data.mode as ReactivateResult['mode'] };
}

/** POST "Opdater bookingsystem" — pusher PRIME-status til salonens bookingsystem. */
export async function syncBooking(handle: string): Promise<SyncResult> {
  const data = await apiPost<{ status: string; http_code: number }>(
    `/portal/prime/members/${enc(handle)}/sync-booking`,
    { body: {} }
  );
  return { status: data.status, httpCode: data.http_code };
}
